import { Box, Container, Skeleton, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { URL, origin } from "../config";
import { useTheme, darken } from "@mui/material/styles";
import Loading from "../components/Loading";
import { Doughnut, Line } from "react-chartjs-2";
import { DataGrid } from "@mui/x-data-grid";

export default function SelfwealthDashboard() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const theme = useTheme();
    const navigate = useNavigate();
    const [lastUpdated, setlastUpdated] = useState();
    const [dailyChange, setDailyChange] = useState();
    const [dailyEarnings, setDailyEarnings] = useState();
    const [totalValue, setTotalValue] = useState();
    const [lineData, setLineData] = useState({});
    const [donutData, setDonutData] = useState({});
    const [gridData, setGridData] = useState([]);

    const green = "linear-gradient(315deg, #00b712 0%, #5aff15 74%)"; // colors for datagrid percentage change
    const red = "linear-gradient(147deg, #f71735 0%, #db3445 74%)";

    const formatter = Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "AUD",
    });

    const dougnutOptions = {
        plugins: {
            title: {
                display: true,
                text: "Selfwealth portfolio breakdown",
                color: theme.palette.primary.contrastText,
            },
            legend: {
                display: true,
                labels: {
                    color: theme.palette.primary.contrastText,
                },
            },
        },
    };

    const lineOptions = {
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                labels: {
                    color: theme.palette.primary.contrastText,
                },
            },
            title: {
                display: true,
                text: "Selfwealth account value over time",
                color: theme.palette.primary.contrastText,
            },
        },
        scales: {
            y: {
                grid: {
                    color: theme.palette.primary.contrastText,
                },
                ticks: {
                    color: theme.palette.primary.contrastText,
                },
            },
            x: {
                ticks: {
                    color: theme.palette.primary.contrastText,
                },
            },
        },
    };

    const gridHeaders = [
        {
            field: "code",
            headerName: "Stock Ticker",
            flex: 1,
        },
        {
            field: "value",
            headerName: "Value AUD",
            flex: 1,
            type: "number",
            valueFormatter: (params) => {
                return formatter.format(params.value);
            },
        },
        {
            field: "units",
            headerName: "No. of units",
            flex: 1,
            type: "number",
        },
        {
            field: "weight",
            headerName: "% of Portfolio",
            flex: 1,
            type: "number",
            valueFormatter: (params) => {
                return params.value !== null
                    ? params.value === 0
                        ? "--"
                        : `${params.value.toFixed(2)}%`
                    : "N/A";
            },
        },
        {
            field: "price",
            headerName: "Current Price",
            flex: 1,
            type: "number",
            valueFormatter: (params) => {
                return formatter.format(params.value);
            },
        },
        {
            field: "percentageChange",
            headerName: "% Change",
            flex: 1,
            type: "number",
            valueFormatter: (params) => {
                return params.value !== null
                    ? params.value === 0
                        ? "--"
                        : `${params.value}%`
                    : "N/A";
            },
            renderCell: (params) => {
                return (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            background:
                                params.value > 0
                                    ? green
                                    : params.value === null
                                    ? "black"
                                    : params.value === 0
                                    ? "grey"
                                    : red,
                            padding: 2,
                            borderRadius: "5px",
                            minWidth: "50px",
                        }}
                    >
                        <span>
                            {params.value !== null
                                ? params.value === 0
                                    ? "--"
                                    : `${params.value > 0 ? "+" : ""}${
                                          params.value
                                      }%`
                                : "N/A"}
                        </span>
                    </div>
                );
            },
        },
    ];

    function fetchData() {
        setLoading(true);
        fetch(`${URL}/fetch/selfwealth`, {
            method: "GET",
            mode: "cors",
            credentials: "include",
            headers: {
                "content-type": "application/json",
                Origin: `${origin}`,
            },
        })
            .then((response) => {
                if (response.ok === false) {
                    navigate(`/${response.status}`);
                    setError(response.statusText);
                    throw new Error(response.statusText);
                }
                return response.json();
            })
            .then((data) => {
                console.log(data);
                setlastUpdated(
                    new Date(data.lastUpdatedOn).toLocaleString("en-US", {
                        weekday: "long",
                        month: "long",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                        hour12: true, // 12-hour time format
                    })
                );
                setDailyChange(data.data.portfolioChange);
                setDailyEarnings(formatter.format(data.data.todaysEarnings));
                setTotalValue(formatter.format(data.data.totalValue));
                createLineData(data);
                createData(data);
                setLoading(false);
            });
    }
    useEffect(() => {
        fetchData();
    }, []);

    function createLineData(data) {
        const totalArray = data.totalHistory.filter((x) => x.selfwealth);
        const lineData = totalArray.map((x) => {
            const a = {};
            a["value"] = x.selfwealth.total;
            a["timestamp"] = x["timestamp"];
            return a;
        });

        const result = lineData.reduce((a, b) => {
            a[b.timestamp.slice(0, 10)] = b.value;
            return a;
        }, {});
        const graph = {
            labels: Object.keys(result),
            datasets: [
                {
                    label: "account value",
                    data: Object.values(result),
                    backgroundColor: "rgba(10, 240, 10, 0.21)",
                    borderColor: "rgba(10, 240, 101)",
                    color: "white",
                    fill: true,
                },
            ],
        };
        setLineData(graph);
    }
    function createColors(str) {
        let r = 0,
            b = 0,
            g = 0;

        for (let i = 0; i < str.length; i++) {
            let code = str.charCodeAt(i);

            r = (r + code * (i * 400)) % 256;
            g = (g + code * 120) % 256;
            b = (b + code * (i * 250)) % 256;
        }

        return "rgb(" + r + "," + g + "," + b + ")";
    }

    function createData(data) {
        const colors = [];
        const portfolio = {};

        const holdings = data.data.portfolioBalances[0].holdings;

        holdings.forEach((stock) => {
            portfolio[stock.name] = stock.value;
            colors.push(createColors(stock.code + "n"));
        });

        setGridData(
            holdings.map((stock, index) => {
                stock.id = index;
                return stock;
            })
        );

        const graph = {
            labels: Object.keys(portfolio),
            datasets: [
                {
                    label: "Value AUD",
                    data: Object.values(portfolio),
                    backgroundColor: colors,
                    borderColor: colors,
                    borderWidth: 1,
                },
            ],
        };
        setDonutData(graph);
    }
    return (
        <>
            <Helmet>
                <title>WealthWatch | Selfwealth</title>
            </Helmet>
            <Container
                maxWidth="xl"
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(12, 1fr)",
                    gridAutoRows: "100px",
                    gap: "20px",
                    "@media (max-width: 75em)": {
                        flexDirection: "column",
                        display: "flex",
                    },
                }}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    sx={{
                        gridColumn: "span 6 ",
                        gridRow: "span 8",
                        backgroundColor: "primary.main",
                        my: 2,
                        borderRadius: 1,
                        boxShadow: 14,
                        outline: `1px solid ${theme.palette.primary.main}`,
                        transition: "outline 1s ease",
                        "&:hover": {
                            outline: `1px solid ${theme.palette.primary.contrastText}`,
                        },
                    }}
                >
                    {loading ? (
                        <Loading />
                    ) : (
                        <Container sx={{ textAlign: "center" }}>
                            <Doughnut
                                data={donutData}
                                options={dougnutOptions}
                            />
                            <Typography
                                color="primary.contrastText"
                                marginTop={1}
                            >
                                Portfolio change of{" "}
                                <Box
                                    component="span"
                                    color={
                                        Number(
                                            dailyChange.substring(
                                                0,
                                                dailyChange.length - 1
                                            )
                                        ) > 0
                                            ? "green"
                                            : "red"
                                    }
                                >{`${dailyChange} ${dailyEarnings}`}</Box>
                            </Typography>
                            <Typography color="highlight">{`Total value: ${totalValue}`}</Typography>
                            <Typography color="grey">{`Last updated on: ${lastUpdated}`}</Typography>
                        </Container>
                    )}
                </Box>
                <Box
                    gridColumn="span 6"
                    gridRow="span 4"
                    marginTop={2}
                    borderRadius={1}
                    boxShadow={14}
                    sx={{
                        backgroundColor: "primary.main",
                        outline: `1px solid ${theme.palette.primary.main}`,
                        transition: "outline 1s ease",
                        "&:hover": {
                            outline: `1px solid ${theme.palette.primary.contrastText}`,
                        },
                    }}
                >
                    <DataGrid
                        rows={gridData}
                        loading={loading}
                        columns={gridHeaders}
                        disableRowSelectionOnClick
                        getRowClassName={(params) => params.row.code}
                        sx={gridData.reduce((total, cur) => {
                            const x = total;
                            x[`& .${cur.code}`] = {
                                backgroundColor: createColors(cur.code + "n"),
                                color: "white",
                                "&:hover": {
                                    backgroundColor: darken(
                                        createColors(cur.code + "n"),
                                        0.3
                                    ),
                                },
                            };
                            return x;
                        }, {})}
                    />
                </Box>
                <Box
                    gridColumn="span 6"
                    gridRow="span 4"
                    marginBottom={2}
                    borderRadius={1}
                    boxShadow={14}
                    sx={{
                        backgroundColor: "primary.main",
                        outline: `1px solid ${theme.palette.primary.main}`,
                        transition: "outline 1s ease",
                        "&:hover": {
                            outline: `1px solid ${theme.palette.primary.contrastText}`,
                        },
                        "@media (max-width: 75em)": {
                            minHeight: "40vh",
                        },
                    }}
                >
                    {loading ? (
                        <Skeleton />
                    ) : (
                        <Line data={lineData} options={lineOptions} />
                    )}
                </Box>
            </Container>
        </>
    );
}
